import { useEffect, useRef, useState, type PointerEvent } from 'react';
import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Avatar } from '../Avatar';
import {
  Archive,
  ArrowLeft,
  AtSign,
  BellOff,
  Mic,
  MoreHorizontal,
  Pin,
  Search,
  Users,
} from 'lucide-react';
import { useAtomValue } from 'jotai';
import { homeSearchOpenAtom } from '../../store';
import type { FamilyMember } from '../../lib/api';
import type { ChatConversation } from './ConversationDialogs';
import { conversationName } from './messageUtils';
import { GettingKeysNotice } from './GettingKeysNotice';
import { NewDeviceBanner } from './NewDeviceBanner';
import { useKeyStorageState } from '../../hooks/useE2EE';
import { InstallNudge } from './InstallNudge';
import { ThanksCard } from './ThanksCard';
import { SiblingNudgeCard } from './SiblingNudgeCard';
import {
  searchIndex,
  snippet,
  type IndexedMessage,
} from '../../lib/searchIndex';
import { appLocale } from '../../lib/format';
import { memberLabelsAtom } from '../../store';
import { isBirthdayToday } from '../../lib/greetings';
import { useT, type MessageKey } from '../../i18n';
import { albumSummary } from '../../lib/albums';
import { gatheringQuote, isKnownType } from './messageUtils';
import { draftPreview, draftsAtom } from '../../lib/drafts';
import { systemMessageText } from '../../lib/deletedAccounts';
import { chatUnread, hasUnread, splitArchived } from '../../lib/chatSettings';
import { isUnbound, UnboundMark } from '../UnboundMark';

type T = ReturnType<typeof useT>;

// What a tile says about an attachment (D146: whole sentences, so Hindi can
// put the verb where it belongs).
const SENT: Record<string, MessageKey> = {
  image: 'chat.home.sentPhoto',
  gif: 'chat.home.sentGif',
  video: 'chat.home.sentVideo',
  voice: 'chat.home.sentVoice',
  file: 'chat.home.sentFile',
};
const SENT_BY: Record<string, MessageKey> = {
  image: 'chat.home.whoSentPhoto',
  gif: 'chat.home.whoSentGif',
  video: 'chat.home.whoSentVideo',
  voice: 'chat.home.whoSentVoice',
  file: 'chat.home.whoSentFile',
};

interface ConversationListProps {
  conversations: ChatConversation[];
  activeId: string | null;
  currentUserId: string | undefined;
  /** Everyone in the family, for the faces row. */
  members: FamilyMember[];
  mutedIds: string[];
  onSelect: (conversationId: string) => void;
  /** Open (or start) a direct chat with this person. */
  onOpenPerson: (userId: string) => void;
  /** Their direct chat, with the birthday card ready (D144). */
  onWishPerson: (userId: string) => void;
  /** Their direct chat, with the Rakhi or Bhai Dooj card ready (D315). */
  onWishSibling: (userId: string, params: Record<string, string>) => void;
  onNewGroup: () => void;
  /** For searching this family's messages on the device (M25). */
  familyId: string | null;
  onOpenMessage: (
    conversationId: string,
    messageId: string,
    createdAt: string
  ) => void;
  /** Unread @mentions (D42), shown as a pill that opens the inbox. */
  unreadMentions: number;
  onOpenMentions: () => void;
  /** ⋯ or press and hold on a tile: the chat's actions (M63). */
  onChatActions: (conversationId: string) => void;
}

const LONG_PRESS_MS = 500;

// Press and hold (touch) or right-click opens a tile's actions; the click
// that ends a long press doesn't also open the chat.
function useLongPress(onLong: (id: string) => void) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fired = useRef(false);
  const cancel = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  return (id: string, onTap: () => void) => ({
    onPointerDown: (event: PointerEvent) => {
      // Reset first: a right-click's long press mustn't swallow the next
      // left click.
      fired.current = false;
      if (event.pointerType === 'mouse') return;
      cancel();
      timer.current = setTimeout(() => {
        fired.current = true;
        onLong(id);
      }, LONG_PRESS_MS);
    },
    onPointerUp: cancel,
    onPointerLeave: cancel,
    onPointerCancel: cancel,
    onPointerMove: (event: PointerEvent) => {
      if (Math.abs(event.movementX) + Math.abs(event.movementY) > 6) cancel();
    },
    onContextMenu: (event: { preventDefault: () => void }) => {
      event.preventDefault();
      cancel();
      fired.current = true;
      onLong(id);
    },
    // The click that ends a long press doesn't also open the chat.
    onClick: () => {
      const longPress = fired.current;
      fired.current = false;
      if (!longPress) onTap();
    },
  });
}

// "10:02" today, "Mon" this week, else "21 Oct".
function shortTime(iso: string, now = new Date()): string {
  const date = new Date(iso);
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString(appLocale(), {
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  const days = (now.getTime() - date.getTime()) / 86_400_000;
  if (days < 6)
    return date.toLocaleDateString(appLocale(), { weekday: 'short' });
  return date.toLocaleDateString(appLocale(), {
    day: 'numeric',
    month: 'short',
  });
}

// Call summaries arrive as the server's fixed English (utils/calls.ts
// callSummary); system lines aren't sealed, so they're translated here (D146).
const CALL_PREVIEW: Record<string, MessageKey> = {
  'Missed voice call': 'chat.call.missedVoice',
  'Missed video call': 'chat.call.missedVideo',
  'Declined voice call': 'chat.home.declinedVoice',
  'Declined video call': 'chat.home.declinedVideo',
  'Cancelled voice call': 'chat.home.cancelledVoice',
  'Cancelled video call': 'chat.home.cancelledVideo',
};

export function systemPreview(text: string, t: T): string {
  const key = CALL_PREVIEW[text];
  if (key) return t(key);
  const done = /^(Voice|Video) call · (.+)$/.exec(text);
  if (done) {
    return t(
      done[1] === 'Voice'
        ? 'chat.call.voiceDuration'
        : 'chat.call.videoDuration',
      { duration: done[2] ?? '' }
    );
  }
  return text;
}

const ALBUM_SENT: Record<'photos' | 'videos' | 'mixed', MessageKey> = {
  photos: 'chat.home.sentPhotos',
  videos: 'chat.home.sentVideos',
  mixed: 'chat.home.sentMixed',
};
const ALBUM_SENT_BY: Record<'photos' | 'videos' | 'mixed', MessageKey> = {
  photos: 'chat.home.whoSentPhotos',
  videos: 'chat.home.whoSentVideos',
  mixed: 'chat.home.whoSentMixed',
};

// "Nani: Who's coming…", "You: We'll be there", "Rhea sent a photo".
export function previewLine(
  conversation: ChatConversation,
  currentUserId: string | undefined,
  t: T
): string | null {
  const last = conversation.lastMessage;
  if (!last) return null;
  if (last.type === 'system') {
    return systemPreview(
      systemMessageText({
        content: last.preview,
        metadata: last.metadata ?? null,
        senderName: last.senderName,
      }),
      t
    );
  }
  const mine = last.senderId === currentUserId;
  const who = mine ? t('common.you') : (last.senderName.split(' ')[0] ?? '');
  const direct = conversation.type === 'direct' && !mine;
  // M60: "Sent 4 photos" (D219), "Poll: Dinner on Sunday?" (D220), and
  // "Message" for a type from a newer Twinden (D222).
  if (last.type === 'album') {
    const { key, n } = albumSummary(last.album ?? { photos: 0, videos: 0 });
    if (n === 0)
      return direct ? t('chat.type.album') : `${who}: ${t('chat.type.album')}`;
    if (direct) return t(ALBUM_SENT[key], { n });
    return mine
      ? `${who}: ${t(ALBUM_SENT[key], { n })}`
      : t(ALBUM_SENT_BY[key], { name: who, n });
  }
  if (last.type === 'poll') {
    const line = last.preview
      ? t('chat.poll.quote', { question: last.preview })
      : t('chat.type.poll');
    return direct ? line : `${who}: ${line}`;
  }
  // M71 (D282): "📅 Dinner".
  if (last.type === 'gathering') {
    const line = last.preview
      ? gatheringQuote(last.preview)
      : t('chat.type.gathering');
    return direct ? line : `${who}: ${line}`;
  }
  if (!isKnownType(last.type)) {
    return direct
      ? t('chat.type.unknown')
      : `${who}: ${t('chat.type.unknown')}`;
  }
  const thing = last.preview.toLowerCase();
  const sent = SENT[last.type];
  const sentBy = SENT_BY[last.type];
  // In a direct chat the tile already names the other person.
  if (conversation.type === 'direct' && !mine) {
    return ['text', 'system'].includes(last.type)
      ? last.preview
      : sent
        ? t(sent)
        : t('chat.home.sentThing', { thing });
  }
  const isAttachment = !['text', 'system'].includes(last.type);
  if (isAttachment && !mine) {
    return sentBy
      ? t(sentBy, { name: who })
      : t('chat.home.whoSentThing', { name: who, thing });
  }
  return `${who}: ${last.preview}`;
}

// The chat home (Living Room): the family's faces, then chats as tiles to
// walk into. "Everyone", the default group (D10), is the big tile.
export function ConversationList({
  conversations,
  activeId,
  currentUserId,
  members,
  mutedIds,
  onSelect,
  onOpenPerson,
  onWishPerson,
  onWishSibling,
  onNewGroup,
  familyId,
  onOpenMessage,
  unreadMentions,
  onOpenMentions,
  onChatActions,
}: ConversationListProps) {
  const searchOpen = useAtomValue(homeSearchOpenAtom);
  const t = useT();
  const keyStore = useKeyStorageState();
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
    else setSearchQuery('');
  }, [searchOpen]);

  const query = searchQuery.trim().toLowerCase();

  // Messages matching the search, from the index on this device (D41).
  const [hits, setHits] = useState<IndexedMessage[]>([]);
  useEffect(() => {
    if (!familyId || query.length < 2) {
      setHits([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      void searchIndex(familyId, query, 30).then((found) => {
        if (!cancelled) setHits(found);
      });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [familyId, query]);
  const memberLabels = useAtomValue(memberLabelsAtom);
  // Unsent drafts (M62, D228) show in place of the last message, except in
  // the chat that's open; they never change the order.
  const drafts = useAtomValue(draftsAtom);
  const draftOf = (conversationId: string) =>
    draftPreview(drafts, conversationId, activeId);
  const draftLine = (text: string, labelColor: string) => (
    <>
      <Text as="span" color={labelColor} fontWeight={700}>
        {t('chat.draft.label')}
      </Text>{' '}
      {text}
    </>
  );
  const chatTitle = (conversationId: string) => {
    const chat = conversations.find((c) => c.id === conversationId);
    if (!chat) return '';
    return chat.isDefault
      ? t('chat.header.everyone')
      : conversationName(chat, currentUserId, memberLabels);
  };
  const filtered = conversations.filter(
    (conversation) =>
      !query ||
      conversationName(conversation, currentUserId, memberLabels)
        .toLowerCase()
        .includes(query)
  );
  // Archived chats wait under one row at the bottom (D231); a search
  // looks through them too.
  const [showArchived, setShowArchived] = useState(false);
  const { main, archived } = splitArchived(conversations);
  useEffect(() => {
    if (showArchived && archived.length === 0) setShowArchived(false);
  }, [showArchived, archived.length]);
  const inArchive = showArchived && !query;
  const shown = query ? filtered : inArchive ? archived : main;
  const everyone = inArchive ? null : (shown.find((c) => c.isDefault) ?? null);
  const others = shown.filter((c) => !c.isDefault);
  const archivedUnread = archived.some(hasUnread);
  const press = useLongPress(onChatActions);
  const moreButton = (conversationId: string, name: string, onDark = false) => (
    <IconButton
      aria-label={t('chat.chatActions.more', { name })}
      icon={<MoreHorizontal size={18} />}
      size="sm"
      variant="ghost"
      colorScheme="gray"
      color={onDark ? 'brand.50' : 'muted'}
      _hover={{ bg: onDark ? 'whiteAlpha.200' : 'surface.subtle' }}
      position="absolute"
      top="6px"
      right="6px"
      borderRadius="full"
      onClick={() => onChatActions(conversationId)}
    />
  );

  // A face gets a saffron ring when their direct chat has something new.
  const unreadFrom = new Set(
    conversations
      .filter((c) => c.type === 'direct' && hasUnread(c))
      .flatMap((c) => c.participants.map((p) => p.id))
  );
  const faces = members.filter((member) => member.userId !== currentUserId);

  // Birthdays today, on this device's date (D144).
  const now = new Date();
  const myBirthday = members.some(
    (member) =>
      member.userId === currentUserId &&
      isBirthdayToday(member.user.birthDay, member.user.birthMonth, now)
  );
  const birthdays = faces.filter((member) =>
    isBirthdayToday(member.user.birthDay, member.user.birthMonth, now)
  );

  return (
    <Flex direction="column" h="full" minH={0}>
      {searchOpen && (
        <Box px={4} pb={2}>
          <InputGroup>
            <InputLeftElement pointerEvents="none" color="muted">
              <Search size={18} />
            </InputLeftElement>
            <Input
              ref={searchRef}
              placeholder={t('chat.home.search')}
              aria-label={t('chat.home.search')}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              borderRadius="full"
            />
          </InputGroup>
        </Box>
      )}

      <Box
        flex="1"
        minH={0}
        overflowY="auto"
        sx={{ overscrollBehaviorY: 'contain' }}
        pb={4}
      >
        {/* Faces */}
        {inArchive && (
          <Box px={4} pt={1} pb={3}>
            <HStack
              as="button"
              type="button"
              spacing={2}
              h="44px"
              onClick={() => setShowArchived(false)}
              aria-label={t('chat.archived.back')}
            >
              <ArrowLeft size={22} aria-hidden="true" />
              <Text
                as="span"
                fontFamily="heading"
                fontWeight={800}
                fontSize="22px"
              >
                {t('chat.archived.title')}
              </Text>
            </HStack>
            <Text fontSize="14px" color="muted" pt={1}>
              {t('chat.archived.hint')}
            </Text>
          </Box>
        )}

        {!query && !inArchive && faces.length > 0 && (
          <HStack
            as="ul"
            listStyleType="none"
            aria-label={t('chat.home.family')}
            spacing="14px"
            px={4}
            pt={2}
            pb="18px"
            overflowX="auto"
            sx={{
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {faces.map((member) => {
              const name =
                memberLabels[member.user.id] ??
                member.nickname ??
                member.user.displayName;
              const fresh = unreadFrom.has(member.userId);
              return (
                <Box as="li" key={member.userId} flexShrink={0}>
                  <VStack
                    as="button"
                    type="button"
                    spacing="6px"
                    w="64px"
                    onClick={() => onOpenPerson(member.userId)}
                    aria-label={t(
                      fresh ? 'chat.home.chatWithNew' : 'chat.home.chatWith',
                      { name }
                    )}
                  >
                    <Avatar
                      name={member.user.displayName}
                      src={member.user.avatarUrl ?? undefined}
                      ownerId={member.userId}
                      familyId={familyId}
                      w="60px"
                      h="60px"
                      fontSize="20px"
                      boxShadow={
                        fresh
                          ? '0 0 0 3px var(--chakra-colors-canvas), 0 0 0 5px var(--chakra-colors-saffron)'
                          : undefined
                      }
                    />
                    <Text
                      fontSize="13px"
                      fontWeight={600}
                      noOfLines={1}
                      maxW="64px"
                    >
                      {name.split(' ')[0]}
                    </Text>
                  </VStack>
                </Box>
              );
            })}
          </HStack>
        )}

        {/* The browser can't keep this device's key: say so here too (D262). */}
        {!query && !inArchive && keyStore !== 'ok' && (
          <NewDeviceBanner variant={keyStore} />
        )}
        {!query && !inArchive && <GettingKeysNotice />}
        {!query && !inArchive && <InstallNudge />}
        {/* Once a month: what families chipped in (M55, D197). */}
        {!query && !inArchive && <ThanksCard />}

        {!query && !inArchive && (myBirthday || birthdays.length > 0) && (
          <VStack
            as="ul"
            listStyleType="none"
            align="stretch"
            spacing={2}
            px={4}
            pb={3}
          >
            {myBirthday && (
              <HStack
                as="li"
                spacing={3}
                minH="56px"
                px={4}
                py={2}
                borderRadius="card"
                bg="saffron"
                color="#1F1D1B"
              >
                <Text as="span" fontSize="26px" aria-hidden="true">
                  🎂
                </Text>
                <Text fontWeight={700} fontSize="17px">
                  {t('chat.home.myBirthday')}
                </Text>
              </HStack>
            )}
            {birthdays.map((member) => {
              const name =
                memberLabels[member.user.id] ??
                member.nickname ??
                member.user.displayName.split(' ')[0];
              return (
                <HStack
                  as="li"
                  key={member.userId}
                  spacing={3}
                  minH="64px"
                  pl={4}
                  pr={2}
                  py={2}
                  borderRadius="card"
                  bg="surface"
                  borderWidth="1px"
                  borderColor="line"
                >
                  <Text as="span" fontSize="26px" aria-hidden="true">
                    🎂
                  </Text>
                  <Text
                    flex="1"
                    minW={0}
                    fontWeight={700}
                    fontSize="16px"
                    lineHeight={1.3}
                  >
                    {t('chat.home.birthdayToday', { name: name ?? '' })}
                  </Text>
                  <Button
                    h="44px"
                    px={4}
                    flexShrink={0}
                    onClick={() => onWishPerson(member.userId)}
                  >
                    {t('chat.home.wishThem')}
                  </Button>
                </HStack>
              );
            })}
          </VStack>
        )}

        {/* Rakhi and Bhai Dooj (M75, D313–D316): its own card, after the birthdays. */}
        {!query && !inArchive && (
          <SiblingNudgeCard
            members={members}
            currentUserId={currentUserId}
            onWish={onWishSibling}
          />
        )}

        {!query && !inArchive && unreadMentions > 0 && (
          <Box px={4} pb={3}>
            <HStack
              as="button"
              type="button"
              onClick={onOpenMentions}
              spacing={2}
              h="40px"
              px={4}
              borderRadius="full"
              bg="accent.tint"
              color="accent"
              fontWeight={700}
              fontSize="15px"
            >
              <AtSign size={18} aria-hidden="true" />
              <Text as="span">
                {unreadMentions === 1
                  ? t('chat.home.mentionsOne')
                  : t('chat.home.mentionsOther', { n: unreadMentions })}
              </Text>
            </HStack>
          </Box>
        )}

        <VStack align="stretch" spacing="10px" px={4}>
          {everyone && (
            <Box position="relative">
              <Box
                as="button"
                type="button"
                textAlign="left"
                w="full"
                {...press(everyone.id, () => onSelect(everyone.id))}
                bg="accent.solid"
                color="accent.onSolid"
                borderRadius="tile"
                px="18px"
                pt="18px"
                pb="16px"
                outline={activeId === everyone.id ? '3px solid' : undefined}
                outlineColor="saffron"
                _hover={{ bg: 'brand.600' }}
              >
                <HStack spacing={2} mb="10px" pr="34px">
                  <Text
                    as="span"
                    flex="1"
                    fontFamily="heading"
                    fontWeight={700}
                    fontSize="22px"
                  >
                    {t('chat.header.everyone')}
                  </Text>
                  {hasUnread(everyone) && (
                    <Text
                      as="span"
                      h="26px"
                      px="10px"
                      borderRadius="full"
                      bg="saffron"
                      color="#1F1D1B"
                      fontSize="13px"
                      fontWeight={700}
                      display="flex"
                      alignItems="center"
                    >
                      {t('chat.home.newCount', { n: chatUnread(everyone) })}
                    </Text>
                  )}
                  {mutedIds.includes(everyone.id) && (
                    <BellOff size={15} aria-label={t('chat.home.muted')} />
                  )}
                </HStack>
                <Text
                  fontSize="17px"
                  lineHeight="24px"
                  color="brand.50"
                  noOfLines={2}
                  mb="10px"
                >
                  {draftOf(everyone.id)
                    ? draftLine(draftOf(everyone.id)!, 'saffron')
                    : (previewLine(everyone, currentUserId, t) ??
                      t('chat.home.helloEveryone'))}
                </Text>
                <Text fontSize="13px" color="gray.200">
                  {t('chat.header.people', { n: everyone.participants.length })}
                  {everyone.lastMessage &&
                    ` · ${shortTime(everyone.lastMessage.createdAt)}`}
                </Text>
              </Box>
              {moreButton(everyone.id, t('chat.header.everyone'), true)}
            </Box>
          )}

          {others.length > 0 && (
            <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap="10px">
              {others.map((conversation) => {
                const name = conversationName(
                  conversation,
                  currentUserId,
                  memberLabels
                );
                const unread = hasUnread(conversation);
                const muted = mutedIds.includes(conversation.id);
                const line = previewLine(conversation, currentUserId, t);
                const draft = draftOf(conversation.id);
                const isVoice =
                  !draft && conversation.lastMessage?.type === 'voice';
                const isActive = activeId === conversation.id;
                const handlers = press(conversation.id, () =>
                  onSelect(conversation.id)
                );
                return (
                  <Box key={conversation.id} position="relative" minW={0}>
                    <Box
                      as="button"
                      type="button"
                      textAlign="left"
                      w="full"
                      h="full"
                      {...handlers}
                      bg="surface"
                      borderWidth="1px"
                      borderColor={isActive ? 'accent' : 'line'}
                      borderRadius="card"
                      p="14px"
                      minH="116px"
                      display="flex"
                      flexDirection="column"
                      gap="6px"
                      _hover={{ borderColor: 'accent' }}
                      aria-label={`${name}${conversation.pinnedAt ? t('chat.home.pinnedSuffix') : ''}${unread ? t('chat.home.unreadSuffix', { n: chatUnread(conversation) }) : ''}${muted ? t('chat.home.mutedSuffix') : ''}`}
                    >
                      <HStack spacing="6px" w="full" pr="28px">
                        <Text
                          as="span"
                          flex="1"
                          fontWeight={700}
                          fontSize="17px"
                          lineHeight={1.2}
                          noOfLines={2}
                        >
                          {name}
                        </Text>
                        {unread && (
                          <Box
                            w="10px"
                            h="10px"
                            borderRadius="full"
                            bg="accent"
                            flexShrink={0}
                            aria-hidden="true"
                          />
                        )}
                        {muted && (
                          <Box color="muted" flexShrink={0} aria-hidden="true">
                            <BellOff size={15} />
                          </Box>
                        )}
                        {conversation.pinnedAt && !inArchive && (
                          <Box
                            color="accent"
                            flexShrink={0}
                            data-testid="pinned-icon"
                            aria-hidden="true"
                          >
                            <Pin size={15} />
                          </Box>
                        )}
                      </HStack>
                      <HStack
                        spacing={1}
                        color={unread ? 'ink.body' : 'muted'}
                        fontSize="15px"
                        lineHeight={1.3}
                        align="flex-start"
                      >
                        {isVoice && (
                          <Box flexShrink={0} pt="2px">
                            <Mic size={16} aria-hidden="true" />
                          </Box>
                        )}
                        <Text
                          as="span"
                          noOfLines={2}
                          color={draft ? 'muted' : undefined}
                        >
                          {draft
                            ? draftLine(draft, 'accent')
                            : (line ??
                              (conversation.type === 'group'
                                ? t('chat.header.people', {
                                    n: conversation.participants.length,
                                  })
                                : t('chat.home.hello')))}
                        </Text>
                      </HStack>
                      {!draft &&
                        conversation.lastMessage &&
                        isUnbound(conversation.lastMessage) && <UnboundMark />}
                    </Box>
                    {moreButton(conversation.id, name)}
                  </Box>
                );
              })}
            </Grid>
          )}

          {!query && !inArchive && archived.length > 0 && (
            <HStack
              as="button"
              type="button"
              onClick={() => setShowArchived(true)}
              spacing={3}
              h="52px"
              px={4}
              borderRadius="card"
              bg="surface"
              borderWidth="1px"
              borderColor="line"
              color="ink"
              fontWeight={700}
              fontSize="16px"
              _hover={{ borderColor: 'accent' }}
              aria-label={`${t('chat.archived.rowCount', { n: archived.length })}${archivedUnread ? t('chat.archived.unreadSuffix') : ''}`}
            >
              <Box color="accent" aria-hidden="true">
                <Archive size={20} />
              </Box>
              <Text as="span" flex="1" textAlign="left">
                {t('chat.archived.rowCount', { n: archived.length })}
              </Text>
              {archivedUnread && (
                <Box
                  w="10px"
                  h="10px"
                  borderRadius="full"
                  bg="accent"
                  aria-hidden="true"
                />
              )}
            </HStack>
          )}

          {query && hits.length > 0 && (
            <Box pt={filtered.length > 0 ? 4 : 0}>
              <Text fontSize="13px" fontWeight={700} color="muted" pb={1}>
                {t('chat.home.messages')}
              </Text>
              <VStack align="stretch" spacing={0}>
                {hits.map((hit) => {
                  const part = snippet(hit.text, query);
                  const mine = hit.senderId === currentUserId;
                  return (
                    <HStack
                      key={hit.id}
                      as="button"
                      type="button"
                      textAlign="left"
                      spacing={3}
                      py="10px"
                      align="flex-start"
                      onClick={() =>
                        onOpenMessage(hit.conversationId, hit.id, hit.createdAt)
                      }
                    >
                      <Avatar
                        size="sm"
                        name={mine ? t('common.you') : hit.senderName}
                      />
                      <Box flex="1" minW={0}>
                        <HStack spacing={2} fontSize="13px" color="muted">
                          <Text
                            as="span"
                            fontWeight={700}
                            color="ink"
                            noOfLines={1}
                          >
                            {mine ? t('common.you') : hit.senderName}
                          </Text>
                          <Text as="span" noOfLines={1} flex="1">
                            {chatTitle(hit.conversationId)}
                          </Text>
                          <Text as="span" flexShrink={0}>
                            {shortTime(hit.createdAt)}
                          </Text>
                        </HStack>
                        <Text fontSize="15px" color="ink.body" noOfLines={2}>
                          {part.before}
                          <Text
                            as="mark"
                            bg="saffron"
                            color="#1F1D1B"
                            borderRadius="4px"
                            px="2px"
                          >
                            {part.match}
                          </Text>
                          {part.after}
                        </Text>
                      </Box>
                    </HStack>
                  );
                })}
              </VStack>
            </Box>
          )}

          {shown.length === 0 && hits.length === 0 && (
            <Center py={12} flexDirection="column" gap={2} color="muted">
              <Text>
                {query
                  ? t('chat.home.nothingMatches', { query: searchQuery.trim() })
                  : t('chat.home.noChats')}
              </Text>
            </Center>
          )}

          {!query && !inArchive && (
            <Box
              as="button"
              type="button"
              onClick={onNewGroup}
              borderRadius="card"
              borderWidth="1px"
              borderStyle="dashed"
              borderColor="line"
              color="muted"
              py="14px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={2}
              fontWeight={600}
              _hover={{ color: 'accent', borderColor: 'accent' }}
            >
              <Users size={18} aria-hidden="true" />
              {t('chat.dialogs.newGroup')}
            </Box>
          )}
        </VStack>
      </Box>
    </Flex>
  );
}
